import { useState, useEffect, useRef } from 'react';
import { walletApi } from '../api/wallet';
import { authApi } from '../api/auth';

// Singleton pattern per gestire il balance globalmente
class BalanceManager {
  constructor() {
    this.balance = null;
    this.listeners = new Set();
    this.loading = false;
    this.intervalId = null;
    this.lastCallTime = 0;
    this.userId = null;
    this.subscriberCount = 0;
  }

  subscribe(callback) {
    this.listeners.add(callback);
    this.subscriberCount++;
    // Notifica immediatamente il valore corrente
    callback(this.balance);
    return () => {
      this.listeners.delete(callback);
      this.subscriberCount--;
      // Se non ci sono più subscriber, ferma il polling
      if (this.subscriberCount === 0) {
        this.stopPolling();
      }
    };
  }

  notifyListeners() {
    this.listeners.forEach(callback => callback(this.balance));
  }

  async loadBalance(userId, options = {}) {
    const isForced = options?.force === true;
    // Prevenire chiamate multiple simultanee
    if (this.loading) {
      return;
    }
    
    // Debounce: prevenire chiamate più frequenti di 20 secondi
    const now = Date.now();
    const timeSinceLastCall = now - this.lastCallTime;
    if (!isForced && timeSinceLastCall < 20000) {
      // Se è passato meno di 20 secondi, ignora completamente la chiamata
      return;
    }
    
    this.loading = true;
    this.lastCallTime = now;
    
    try {
      const response = await walletApi.getBalance(userId, { force: isForced });
      if (response?.success && response.balance) {
        this.balance = response.balance.d10s || 0;
        this.notifyListeners();
      }
    } catch (error) {
      // Ignora completamente gli errori di rate limiting
      if (error.message?.startsWith('RATE_LIMIT:') || error.response?.status === 429) {
        // Silently fail - non fare nulla
        return;
      }
      // Per altri errori, mantieni il balance corrente
    } finally {
      this.loading = false;
    }
  }

  startPolling(userId) {
    // DISABILITATO TEMPORANEAMENTE - Il polling causa troppe richieste
    // Carica il balance solo una volta all'avvio, poi niente polling automatico
    if (this.intervalId && this.userId === userId) {
      return;
    }

    this.stopPolling();
    this.userId = userId;
    if (this.subscriberCount > 0 && this.userId === userId) {
      this.loadBalance(userId);
    }
    
    // POLLING DISABILITATO - Non fare più polling automatico
    // Il balance verrà aggiornato solo quando necessario (es. dopo deposit/withdraw)
  }

  stopPolling() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.userId = null;
  }

  refresh(userId, options = {}) {
    this.loadBalance(userId, { force: true, ...options });
  }

  /**
   * Imposta il saldo mostrato in navbar da un valore già noto (es. GET /users dopo asta),
   * senza chiamare il wallet: evita debounce e allinea navbar al resto dell'app.
   */
  setBalanceFromProfile(amount) {
    const n = parseFloat(String(amount));
    if (!Number.isFinite(n)) return;
    this.balance = n;
    this.notifyListeners();
  }
}

// Istanza singleton
const balanceManager = new BalanceManager();

/**
 * Hook per gestire il balance dell'utente
 * Garantisce che solo una chiamata alla volta venga fatta, anche se l'hook viene usato in più componenti
 */
export const useBalance = () => {
  const [balance, setBalance] = useState(null);
  const [user, setUser] = useState(() => authApi.getCurrentUser());

  useEffect(() => {
    // Aggiorna user quando cambia in localStorage
    const handleStorageChange = () => {
      const currentUser = authApi.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const balanceUserId = user?.isAdmin ? user?.linkedUserId : user?.id;

    if (!balanceUserId) {
      balanceManager.stopPolling();
      setBalance(null);
      return;
    }

    // Sottoscrivi agli aggiornamenti del balance
    const unsubscribe = balanceManager.subscribe(setBalance);
    
    // Avvia il polling (solo se non è già attivo) - carica una volta subito
    balanceManager.startPolling(balanceUserId);

    return () => {
      unsubscribe();
    };
  }, [user?.id, user?.linkedUserId, user?.isAdmin]);

  return balance;
};

export const refreshBalance = (userId) => {
  if (!userId) return;
  balanceManager.refresh(userId, { force: true });
};

/** Allinea la navbar al saldo restituito dal profilo utente (es. dopo polling marketplace). */
export const syncBalanceFromProfile = (d10sBalance) => {
  balanceManager.setBalanceFromProfile(d10sBalance);
};

